/**
 * src/state/store.ts — central app state (Zustand).
 *
 * Holds the loaded SOFR data, UI prefs, the working scenario inputs, the live
 * projection result, and the saved-scenario library. Inputs are validated/normalized
 * through `scenarioInputsSchema` before every projection (§8.2, §10.2), and the engine
 * only ever receives a parsed object. The current edited scenario is NOT auto-persisted
 * until saved.
 */
import { create } from 'zustand';
import { scenarioInputsSchema } from '@/schema';
import type {
  MonteCarloResult,
  ProjectionResult,
  Scenario,
  ScenarioInputs,
  TaxProfile,
  TornadoBar,
  UiState,
} from '@/types';
import { defaultScenarioInputs } from '@/types';
import { containsNonFinite, project, runProjection } from '@/engine';
import { loadSofr } from '@/data/loaders';
import { getTaxProfile, getUiState, setTaxProfile, clearTaxProfile, setUiState } from '@/db/appState';
import {
  deleteScenario,
  duplicateScenario,
  listScenarios,
  newScenario,
  renameScenario,
  saveScenario,
} from '@/db/scenarios';

export interface ComputeOutput {
  result: ProjectionResult | null;
  issues: string[];
}

/** Validate inputs, run the engine, and guard the output (§8.2). Pure — testable alone. */
export function computeResults(inputs: ScenarioInputs): ComputeOutput {
  const parsed = scenarioInputsSchema.safeParse(inputs);
  if (!parsed.success) {
    return { result: null, issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`) };
  }
  const borrow = runProjection(parsed.data);
  const nonFinite = containsNonFinite(borrow);
  if (nonFinite.length > 0) {
    return { result: null, issues: [`Projection produced invalid numbers: ${nonFinite[0]}`] };
  }
  return { result: project(parsed.data), issues: [] };
}

const RECOMPUTE_DEBOUNCE_MS = 150;

interface AppState {
  // Loaded data
  /** True once init() has resolved — gates rendering that needs the seeded rate (§10.9). */
  loaded: boolean;
  sofrLatestRate: number;
  sofrLatestDate: string;
  dataSource: { sofr: string };
  dataWarnings: string[];

  // UI prefs (persisted)
  ui: UiState;

  // Working scenario
  inputs: ScenarioInputs;
  currentScenarioId: string | null;
  currentName: string;
  dirty: boolean;
  readOnly: boolean;

  // Live results
  result: ProjectionResult | null;
  resultIssues: string[];

  // Analysis results
  mcResult: MonteCarloResult | null;
  tornado: TornadoBar[] | null;

  // Library
  scenarios: Scenario[];
  readOnlyIds: string[];
  quarantinedCount: number;
  taxProfile: TaxProfile | null;

  // Actions
  init: () => Promise<void>;
  setInputs: (patch: Partial<ScenarioInputs>) => void;
  recomputeNow: () => void;
  setUi: (patch: Partial<UiState>) => Promise<void>;
  refreshScenarios: () => Promise<void>;
  loadScenario: (id: string) => Promise<void>;
  newBlank: () => void;
  loadInputs: (inputs: ScenarioInputs, name?: string) => void;
  saveCurrentAs: (name: string) => Promise<void>;
  updateCurrent: () => Promise<void>;
  removeScenario: (id: string) => Promise<void>;
  duplicate: (id: string) => Promise<void>;
  rename: (id: string, name: string) => Promise<void>;
  saveTaxProfileFromCurrent: () => Promise<void>;
  clearTaxProfileDefaults: () => Promise<void>;
  setMcResult: (r: MonteCarloResult | null) => void;
  setTornado: (t: TornadoBar[] | null) => void;
  toggleCompare: (id: string) => Promise<void>;
}

let recomputeTimer: ReturnType<typeof setTimeout> | null = null;

export const useStore = create<AppState>((set, get) => ({
  loaded: false,
  sofrLatestRate: 0,
  sofrLatestDate: '',
  dataSource: { sofr: '' },
  dataWarnings: [],
  ui: { theme: 'system', complexityMode: 'simple', onboardingComplete: false, lastScenarioId: null, comparisonSelection: [], showRealDollars: false },
  inputs: defaultScenarioInputs(0),
  currentScenarioId: null,
  currentName: 'Untitled scenario',
  dirty: false,
  readOnly: false,
  result: null,
  resultIssues: [],
  mcResult: null,
  tornado: null,
  scenarios: [],
  readOnlyIds: [],
  quarantinedCount: 0,
  taxProfile: null,

  init: async () => {
    const [sofr, ui, taxProfile] = await Promise.all([
      loadSofr(),
      getUiState(),
      getTaxProfile(),
    ]);
    const inputs = defaultScenarioInputs(sofr.latestRate, taxProfile);
    set({
      loaded: true,
      sofrLatestRate: sofr.latestRate,
      sofrLatestDate: sofr.latestDate,
      dataSource: { sofr: sofr.source },
      dataWarnings: [...sofr.warnings],
      ui,
      taxProfile,
      inputs,
    });
    await get().refreshScenarios();
    get().recomputeNow();
  },

  setInputs: (patch) => {
    // Editing inputs invalidates any prior Monte Carlo / sensitivity results.
    set((s) => ({ inputs: { ...s.inputs, ...patch }, dirty: true, mcResult: null, tornado: null }));
    if (recomputeTimer) clearTimeout(recomputeTimer);
    recomputeTimer = setTimeout(() => get().recomputeNow(), RECOMPUTE_DEBOUNCE_MS);
  },

  recomputeNow: () => {
    const { result, issues } = computeResults(get().inputs);
    set({ result, resultIssues: issues });
  },

  setUi: async (patch) => {
    const ui = { ...get().ui, ...patch };
    set({ ui });
    await setUiState(ui);
  },

  refreshScenarios: async () => {
    const { scenarios, readOnlyIds, quarantined } = await listScenarios();
    set({ scenarios, readOnlyIds, quarantinedCount: quarantined });
  },

  loadScenario: async (id) => {
    const found = get().scenarios.find((s) => s.id === id);
    if (!found) return;
    set({
      inputs: found.inputs,
      currentScenarioId: found.id,
      currentName: found.name,
      dirty: false,
      readOnly: get().readOnlyIds.includes(found.id),
    });
    get().recomputeNow();
  },

  newBlank: () => {
    set({
      inputs: defaultScenarioInputs(get().sofrLatestRate, get().taxProfile),
      currentScenarioId: null,
      currentName: 'Untitled scenario',
      dirty: false,
      readOnly: false,
    });
    get().recomputeNow();
  },

  loadInputs: (inputs, name = 'Untitled scenario') => {
    set({ inputs, currentScenarioId: null, currentName: name, dirty: false, readOnly: false, mcResult: null, tornado: null });
    get().recomputeNow();
  },

  saveCurrentAs: async (name) => {
    const saved = await saveScenario(newScenario(name, get().inputs));
    set({ currentScenarioId: saved.id, currentName: saved.name, dirty: false, readOnly: false });
    await get().refreshScenarios();
  },

  updateCurrent: async () => {
    const { currentScenarioId, currentName, inputs } = get();
    if (!currentScenarioId) return;
    await saveScenario({
      id: currentScenarioId,
      name: currentName,
      inputs,
      createdAt: get().scenarios.find((s) => s.id === currentScenarioId)?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      schemaVersion: 2,
    });
    set({ dirty: false });
    await get().refreshScenarios();
  },

  removeScenario: async (id) => {
    await deleteScenario(id);
    if (get().currentScenarioId === id) get().newBlank();
    await get().refreshScenarios();
  },

  duplicate: async (id) => {
    await duplicateScenario(id);
    await get().refreshScenarios();
  },

  rename: async (id, name) => {
    await renameScenario(id, name);
    if (get().currentScenarioId === id) set({ currentName: name });
    await get().refreshScenarios();
  },

  saveTaxProfileFromCurrent: async () => {
    const profile = get().inputs.tax;
    await setTaxProfile(profile);
    set({ taxProfile: profile });
  },

  clearTaxProfileDefaults: async () => {
    await clearTaxProfile();
    set({ taxProfile: null });
  },

  setMcResult: (r) => set({ mcResult: r }),
  setTornado: (t) => set({ tornado: t }),

  toggleCompare: async (id) => {
    const current = get().ui.comparisonSelection;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : current.length < 3
        ? [...current, id]
        : current;
    await get().setUi({ comparisonSelection: next });
  },
}));
